import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { fabric } from 'fabric';
import { Subject, Observable, takeUntil, filter, take } from 'rxjs';
import { CanvasService } from '@core/services/fabric/canvas.service';
import { ColorsCountService } from '@core/services/colors/colors-count.service';
import { FinalColor } from '@core/models/colors';
import { DecorationTypeEnum, Designs, DesignToolCanvas, DesignToolPosition, Dimensions, TransformedFiles, VariantColors, VariantImages } from '@core/models/designTool';
import { Store } from '@ngrx/store';
import * as fromDesignTool from '@core/state/design-tool/design-tool.selectors';
import { deleteDesignPositionObject, getDesignToolCanvas, setDesignObject, updateDesignPositions, updateDesinPositionObject } from '@core/state/design-tool/design-tool.actions';
import { DownloadOptionsModalComponent } from './download-options-modal/download-options-modal.component';
import { MdbModalService } from 'mdb-angular-ui-kit/modal';
import { EditPositionNotesModalComponent } from './edit-position-notes-modal/edit-position-notes-modal.component';
import { ActivatedRoute } from '@angular/router';
import { ProductDesignLibraryModalComponent } from '../product-design-library/product-design-library-modal/product-design-library-modal.component';
// import { CdkDragDrop } from '@angular/cdk/drag-drop';

@Component({
  selector: 'product-design-tool',
  templateUrl: './product-design-tool.component.html',
  styleUrls: ['./product-design-tool.component.scss'],
})
export class ProductDesignToolComponent implements OnInit, OnDestroy, AfterViewInit {
  mobileView$ = this.store.select(fromDesignTool.selectMobileView);
  mobileView: boolean;
  private accumulatedColors: Map<string, FinalColor> = new Map<string, FinalColor>();
  canvasWidth: number = 220;
  canvasHeight: number = 400; 
  trashIconCanvas: HTMLCanvasElement;
  iconSize: number = 32;
  colors: any[] = [];
  colorPickerColors: string[] = [];
  colorPickerLabel = "Design Colors";
  colorPickerFirstColor: string;
  selectedDecorationType: DecorationTypeEnum = DecorationTypeEnum.Print;
  activePosition$: Observable<string> = this.store.select(fromDesignTool.selectCurrentPosition);
  canCustomize$: Observable<boolean> = this.store.select(fromDesignTool.selectCanCustomize).pipe(filter(e => e != undefined));
  canCustomize: boolean;
  position: string = 'front';
  canvasDimensions$: Observable<DesignToolCanvas> = this.store.select(fromDesignTool.selectCanvasDimensions);
  canvasDimensions: DesignToolCanvas;
  selectCurrentPositionData$: Observable<Partial<DesignToolPosition>> = this.store.select(fromDesignTool.selectCurrentPositionData);
  selectCurrentPositionData: DesignToolPosition;
  types = ["Print", "Embroidery"];
  brand: string;
  style: string;
  color: string;
  private destroy$ = new Subject<void>();
  @Input() variant: VariantColors;
  @Input() designMockups: VariantImages;
  @Input() cartId: string;
  @Input() designObject: Designs;
  @Input() designFiles: TransformedFiles[];
  @Output() uploadArtEvent = new EventEmitter<string>();
  @Output() selectedDesignFile = new EventEmitter<{ files: TransformedFiles|TransformedFiles[], data?: { width: number; height: number; left: number; top: number}}>();
  @Output() decorationType = new EventEmitter<string>();
  @Output() selectedPosition = new EventEmitter<string>();
  @ViewChild('productImage') productImage?: ElementRef<HTMLImageElement>;
  @ViewChild('mockupContainer') mockupContainer?: ElementRef<HTMLImageElement>;

  constructor(
    private renderer: Renderer2,
    private canvasService: CanvasService,
    private colorsCountService: ColorsCountService,
    private store: Store,
    private modalService: MdbModalService,
    private route: ActivatedRoute
  ) { }

  ngAfterViewInit(): void {
    
  }

  ngOnInit(): void {
    // TODO: live react to viewport size changes: when rotating mobile device or when modifying windows size on desktop
    // fromEvent(window, 'resize')
    //   .pipe(
    //     debounceTime(1000),
    //     takeUntil(this.destroy$)
    //   ).subscribe((evt: any) => {
    //     this.fetchCanvasDimensions()
    //     setTimeout(() => this.displayImagesForPosition(), 500)
    //   });
    this.brand = this.route.snapshot.params.brand;
    this.style = this.route.snapshot.params.style;
    this.color = this.route.snapshot.queryParams?.color;
    this.mobileView$.pipe(takeUntil(this.destroy$)).subscribe(mobileView => {
      this.mobileView = mobileView
    })
    this.canvasService.objectDeleted.pipe(
      takeUntil(this.destroy$)
    ).subscribe((imageUrl: string) => {
      this.store.dispatch(deleteDesignPositionObject({ position: this.position, imageUrl }))
    })

    this.canvasService.objectLocked.pipe(takeUntil(this.destroy$)).subscribe(imageUrl => {
      const images = [...this.selectCurrentPositionData.canvas]
      const foundImageIndex = images.findIndex(e => e.url == imageUrl)
      if (foundImageIndex > -1) {
        images[foundImageIndex] = {...images[foundImageIndex], editable: false}
      }
      this.store.dispatch(updateDesinPositionObject({ position: this.position, design: images, decorationType: this.selectedDecorationType }));
    })

    this.canvasService.objectEdited.pipe(takeUntil(this.destroy$)).subscribe(imageUrl => {
      const images = [...this.selectCurrentPositionData.canvas]
      const foundImageIndex = images.findIndex(e => e.url == imageUrl)
      if (foundImageIndex > -1) {
        images[foundImageIndex] = {...images[foundImageIndex], editable: true}
      }
      this.store.dispatch(updateDesinPositionObject({ position: this.position, design: images, decorationType: this.selectedDecorationType }));
    })

    this.canvasService.rotationEnded.pipe(
      takeUntil(this.destroy$)
    ).subscribe((rotatedImage: fabric.Image) => {
      if (rotatedImage && rotatedImage.type === 'image') {
        const imageUrl = decodeURIComponent(rotatedImage.getSrc());
        const images = [...this.selectCurrentPositionData.canvas]
        const foundImageIndex = images.findIndex(e => e.url == imageUrl)
        if (foundImageIndex > -1) {
          images[foundImageIndex] = {...images[foundImageIndex], rotation: rotatedImage.angle, coordinates: {x: rotatedImage.left, y: rotatedImage.top}, originX: 'center', originY: 'center'}
          this.store.dispatch(updateDesinPositionObject({ position: this.position, design: images, decorationType: this.selectedDecorationType }));
        }
      }
    });
    this.activePosition$.pipe(takeUntil(this.destroy$)).subscribe(activePosition => {
      this.position = activePosition
      setTimeout(() => this.displayImagesForPosition(), 200)
    })
    this.canvasDimensions$.pipe(takeUntil(this.destroy$)).subscribe((canvasDimensions: DesignToolCanvas) => {
      this.canvasDimensions = canvasDimensions
    })
    this.selectCurrentPositionData$.pipe(takeUntil(this.destroy$)).subscribe((selectCurrentPositionData: DesignToolPosition) => {
      this.selectCurrentPositionData = selectCurrentPositionData ? JSON.parse(JSON.stringify(selectCurrentPositionData)) : selectCurrentPositionData
      if (this.selectCurrentPositionData && !this.selectCurrentPositionData.decorationType) {
        this.selectDecorationType(DecorationTypeEnum.Print)
      } else {
        this.selectedDecorationType = this.selectCurrentPositionData?.decorationType ?? DecorationTypeEnum.Print
      }
      
      this.handleColorPickerCount()
    })
    this.canCustomize$.pipe(takeUntil(this.destroy$)).subscribe(async (canCustomize: boolean) => {
      this.canCustomize = canCustomize
      setTimeout(() =>this.displayImagesForPosition(), 200)
    })
  }

  fetchCanvasDimensions() {
    if (!this.productImage || !this.brand || !this.style || !this.color) {
      console.warn('Not enough data to fetch canvas dimensions')
      return
    }
    const brand = this.brand
    const style = this.style
    const color = this.color
    const width = this.productImage.nativeElement.clientWidth

    const data = { brand, style, color, width };
    this.store.dispatch( getDesignToolCanvas({data}))
  }

  hideCanvas() {
    const canvasElement = this.canvasService.canvas?.getElement() as HTMLCanvasElement;
    if (!canvasElement) {
      console.error('canvas element not found')
      return;
    }
    const canvasContainer = canvasElement.parentNode as HTMLElement;
    this.canvasService.hideCanvas(canvasContainer, this.renderer)
  }

  private tryInitializeCanvas(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      // Dispose the existing canvas if it foundImageIndex
      if (this.canvasDimensions && this.variant && this.position && this.productImage) {
        const dimensions: Dimensions = {...this.canvasDimensions[this.position]}
        const result = this.canvasService.initializeCanvas('fabricCanvas', dimensions, this.onImageMoved.bind(this), this.onImageScaled.bind(this));
        resolve(result)
      }
      resolve(false)
    })
  }

  /**
   * Method to set up the canvas using the service
   * @returns
   */
  setUpCanvas() {
    if(!this.canvasService.canvas) {
      console.error('no canvas object created yet')
      return;
    }
    const canvasElement = this.canvasService.canvas.getElement() as HTMLCanvasElement;
    if (!canvasElement) {
      console.error('no canvas html element created yet')
      return;
    }
    const canvasContainer = canvasElement.parentNode as HTMLElement;
    const borderColor = this.canvasDimensions.borderColor
    const dimensions: Dimensions = {...this.canvasDimensions[this.position]}
    // if (this.mobileView) {
    //   const imageWidth = this.productImage.nativeElement.clientWidth
    //   dimensions.top = ((imageWidth * dimensions.top) / 700)
    //   dimensions.left = ((imageWidth * dimensions.left) / 776) - 5 // we use -5 to subtract 5 pixels from the left positioning. Why? Idk, otherwise we loose precision compared to desktop
    // }

    // Update canvas styling
    this.canvasService.setCanvasStyles(canvasContainer, this.renderer, dimensions, borderColor)
  }

  onImageScaled(activeObject: fabric.Image){
    const scaleX = activeObject.scaleX;
    const scaleY = activeObject.scaleY;
    const width = activeObject.width
    const height = activeObject.height
    const activeSrc = decodeURIComponent(activeObject.getSrc());
    const images = [...this.selectCurrentPositionData.canvas]
    const foundImageIndex = images.findIndex(e => e.url == activeSrc)
    if (foundImageIndex > -1) {
      images[foundImageIndex] = {...images[foundImageIndex], scaleX, scaleY, width, height, shouldResize: false, scaledToMobile: this.mobileView, imageClientWidth: this.canvasService.productImageClientWidth, originX: 'center', originY: 'center'}
      this.store.dispatch(updateDesinPositionObject({ position: this.position, design: images, decorationType: this.selectedDecorationType }));
    }
  }

  onImageMoved(activeObject: fabric.Image) {
    // Getting the source of the image
    const activeSrc = decodeURIComponent(activeObject.getSrc());
    const images = [...this.selectCurrentPositionData.canvas]
    const foundImageIndex = images.findIndex(e => e.url == activeSrc)
    if (foundImageIndex > -1) {
      images[foundImageIndex] = {...images[foundImageIndex], coordinates: {x: activeObject.left, y: activeObject.top}, scaleX: activeObject.scaleX, scaleY: activeObject.scaleY, shouldResize: false, scaledToMobile: this.mobileView, imageClientWidth: this.canvasService.productImageClientWidth, originX: 'center', originY: 'center'}
      this.store.dispatch(updateDesinPositionObject({ position: this.position, design: images, decorationType: this.selectedDecorationType }));
    }
  }

  exportCanvasAsImage(fabricCanvas: fabric.Canvas, filename, desiredWidth) {
    // remove non-image objects
    fabricCanvas.forEachObject((object, index) => {
      if (!(object instanceof fabric.Image)) {
        fabricCanvas.remove(object)
      }
    })

    // Export the canvas as an image
    const dataURL = fabricCanvas.toDataURL({
        format: 'png',
        quality: 1, // for JPEG, but setting it for consistency
        multiplier: desiredWidth / fabricCanvas.getWidth(), // scale multiplier
    });

    // Create a download link and trigger the download
    const downloadLink = document.createElement('a');
    downloadLink.href = dataURL;
    downloadLink.download = filename;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  async displayImagesForPosition() {
    if (!this.productImage) {
      return
    }
    let canvasInitialized = !!this.canvasService.canvas

    canvasInitialized = await this.tryInitializeCanvas()
    
    if (!canvasInitialized) {
      return
    }
    this.canvasService.canvas?.remove(...this.canvasService.canvas.getObjects());
    if (!this.canCustomize) {
      this.hideCanvas()
      return
    }
    this.setUpCanvas();
    // add each image to the canvas
    this.selectCurrentPositionData?.canvas?.forEach(async imageInfo => {
      try {
        await this.canvasService.addImageToCanvas({...imageInfo}, false, undefined, false);

        // check the editable status of the image
        if (imageInfo.editable){
          this.canvasService.editImagePosition(imageInfo.url);
        } else {
          this.canvasService.lockImagePosition(imageInfo.url);
        }
      } catch(error){
        console.error('Error in displayImagesForPosition:', error);
      }
    })
    this.canvasService.canvas.requestRenderAll()
    this.handleColorPickerCount()
  }

  get accumulatedColorsArray(): FinalColor[] {
    return Array.from(this.accumulatedColors.values());
  }

  /**
   * Method that will download the canvas as an image in 13" x 18" size which is real size of the product
   */
  exportArtMockup() {
    this.canvasService.canvas.clone((cloned) => this.exportCanvasAsImage(cloned, `${this.position}-canvasLifesize-mock.jpg`, 3900))
  }

  @HostListener('window:keydown', ['$event'])
  // event.code === 'Backspace'
  handleKeyboardEvent(event: KeyboardEvent) {
      if (event.code === 'Delete') {
        const activeObject = this.canvasService.canvas.getActiveObject() as fabric.Image;
        if(activeObject){
          this.canvasService.deleteObject(decodeURIComponent(activeObject.getSrc()));
        }
      }
  }

  selectDecorationType(type: DecorationTypeEnum) {
    this.selectedDecorationType = type
    this.selectCurrentPositionData.decorationType = type
    this.decorationType.emit(type)
    const images = [...this.selectCurrentPositionData.canvas]
    this.store.dispatch(updateDesinPositionObject({ position: this.position, design: images, decorationType: type }));
  }

  async handleColorPickerCount() {
    let sum = 0
    this.accumulatedColors = new Map<string, FinalColor>();
      // Check if the position foundImageIndex in the map
    for(const image of this.selectCurrentPositionData?.canvas ?? [])  {
      sum += image.colorCount;
      await this.getColorCount(image.url)
    }
    this.colorPickerColors = Array.from(this.accumulatedColors.values()).map(colorObj => colorObj.hex);
    this.colorPickerFirstColor = this.colorPickerColors[0];
  }

  async getColorCount(imageUrl: string) {
    try {
      const imageColors: FinalColor[] = await this.colorsCountService.getImageColors(imageUrl)
      imageColors.forEach(color => {
        if (!this.accumulatedColors.has(color.hex)) {
            this.accumulatedColors.set(color.hex, color);
        }
      });
      return imageColors.length;
    } catch (error) {
      console.error("Error in getColorCount:", error);
    }
  }

  exportProductMockup() {
    this.store.select(fromDesignTool.selectDesignObject).pipe(take(1)).subscribe(designObject => {
      this.canvasService.exportProductMockup(designObject, this.position, this.canvasDimensions, this.variant, this.designMockups, this.renderer)
    })
  }

  // Unsubscribe from the destroy$ observable when the component is destroyed
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(setDesignObject({ designObject: {} }));
  }

  onNextPosition() {
    const currentPositionIndex = this.designObject.positions.findIndex(e => e.position == this.position)
    const nextPositionIndex = this.designObject.positions?.[currentPositionIndex + 1]?.position ? currentPositionIndex + 1 : 0
    this.selectedPosition.emit(this.designObject.positions?.[nextPositionIndex]?.position);
  }

  onPreviousPosition() {
    const currentPositionIndex = this.designObject.positions.findIndex(e => e.position == this.position)
    const nextPositionIndex = this.designObject.positions?.[currentPositionIndex - 1]?.position ? currentPositionIndex - 1 : (this.designObject?.positions?.length > 0 ? this.designObject.positions.length - 1 : 0)
    this.selectedPosition.emit(this.designObject.positions?.[nextPositionIndex]?.position);
  }

  onOpenDownloadOptions() {
    this.modalService.open(DownloadOptionsModalComponent, {
      containerClass: 'frame bottom',
      modalClass: 'modal-frame modal-bottom'
    }).onClose.subscribe(resp => {
      if (resp == 1) {
        this.exportProductMockup()
      } else if (resp == 2) {
        this.exportArtMockup()
      }
    })
  }

  onOpenLibrary() {
    const ref = this.modalService.open(ProductDesignLibraryModalComponent)
    ref.component.openUploadArtModal = (data) => this.uploadArtEvent.emit(data)
    ref.component.handleImageClick = (data) => this.selectedDesignFile.emit({files: data})
    ref.onClose.subscribe(data => {
      if (data?.length > 0) {
        this.selectedDesignFile.emit({files: data})
      }
    })
  }

  onAddNote() {
    if (!this.selectCurrentPositionData || !this.designObject) {
      return
    }
    this.designObject.positions
    this.modalService.open(EditPositionNotesModalComponent, {
      containerClass: 'frame bottom',
      modalClass: 'modal-frame modal-bottom',
      data: {
        position: this.position,
        notes: this.designObject.positions.find(e => e.position == this.position)?.notes
      }
    }).onClose.subscribe(resp => {
      if (resp) {
        const positionsCopy = [...this.designObject.positions.map(e => ({...e}))]
        const positionData = positionsCopy.find(e => e.position == this.position)
        if (positionData) {
          positionData.notes = resp
        }
        this.store.dispatch(updateDesignPositions({ designId: this.designObject._id, positions: positionsCopy }));
      }
    })
  }

  onLoadProductImage() {
    //this.viewPortSizeChange.next('')
    this.fetchCanvasDimensions()
    this.canvasService.productImageClientWidth = this.productImage.nativeElement.clientWidth
    this.canvasService.productImageClientHeight = this.productImage.nativeElement.clientHeight
    this.displayImagesForPosition()
  }

  onDrop(event: DragEvent) {
    // Copy the item instead of moving it
    const json = event.dataTransfer.getData('design')
    try {
      JSON.parse(json)
    } catch (error) {
      return
    }
    const copiedItem: TransformedFiles = JSON.parse(json);

    const canvasElementRect = this.canvasService.canvas?.getElement()?.getBoundingClientRect();

    if (!canvasElementRect) return
    
    const canvasLeft = canvasElementRect.left
    const canvasTop = canvasElementRect.top
    
    // Calculate relative drop coordinates
    const scaledDropX = event.x - canvasLeft;
    const scaledDropY = event.y - canvasTop;
    const dragOffset = copiedItem.dragOffset;
    const width = copiedItem.width;
    const height = copiedItem.height;
    const imageHeight = copiedItem.imageHeight;
    const imageWidth = copiedItem.imageWidth;

    // Adjust the drop coordinates by the drag offset
    const dropXWithOffset = scaledDropX + dragOffset.x;
    const dropYWithOffset = scaledDropY + dragOffset.y;
    const data = { width, height, left: dropXWithOffset < 0 ? 0 : dropXWithOffset, top: dropYWithOffset < 0 ? 0 : dropYWithOffset, imageWidth, imageHeight}
    copiedItem.fromDrop = true
    this.selectedDesignFile.emit({files: [copiedItem], data})
  }
}
